import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { backendApi, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { StatCard } from '../components/Icon';
import { fmtDateTime } from '../lib/dates';
import {
  UsageChart, GrowthChart, SparkCard, WINDOWS,
  type WindowKey, type UsageDay, type GrowthDay,
} from '../components/Charts';

/**
 * NOTE: /license/admin/dashboard returns camelCase plus its own `stats` block —
 * a different shape from /license/admin/accounts (which is snake_case).
 * Charts pull from separate endpoints: /growth and /usage-all.
 */
interface DashAccount {
  id: string; email: string; name?: string | null; company?: string | null;
  plan?: string | null; planName?: string | null; status: string;
  creditsBalance?: number; creditsUsed?: number;
  sitesCount?: number | string; maxSites?: number;
  accountType?: string | null; createdAt?: string;
}
interface Stats {
  totalAccounts?: number; customerAccounts?: number;
  customerCreditsUsed?: number; totalCreditsUsed?: number;
  totalCreditsBalance?: number; totalSites?: number;
  accountsByType?: Record<string, number>;
}

/** 'all' maps to a very wide day window on the backend. */
const daysParam = (w: WindowKey) => (w === 'all' ? 3650 : Number(w));

function WindowToggle({ value, onChange }: { value: WindowKey; onChange: (w: WindowKey) => void }) {
  return (
    <div className="seg">
      {WINDOWS.map((w) => (
        <button key={w.key} className={'seg-btn' + (value === w.key ? ' on' : '')}
          onClick={() => onChange(w.key)}>{w.label}</button>
      ))}
    </div>
  );
}

// Active-users section has its own shorter windows (Today is the default).
interface ActiveAccount {
  id: string; email: string; name?: string | null; plan_id?: string | null;
  status: string; account_type?: string | null;
  last_active: string; credits_used: number | string; events: number | string;
}
const ACTIVE_WINDOWS: { key: string; label: string }[] = [
  { key: '1', label: 'Today' }, { key: '3', label: '3d' }, { key: '7', label: '7d' }, { key: '30', label: '30d' },
];
function ActiveWindowToggle({ value, onChange }: { value: string; onChange: (w: string) => void }) {
  return (
    <div className="seg">
      {ACTIVE_WINDOWS.map((w) => (
        <button key={w.key} className={'seg-btn' + (value === w.key ? ' on' : '')}
          onClick={() => onChange(w.key)}>{w.label}</button>
      ))}
    </div>
  );
}

export default function Overview() {
  const { can } = useAuth();
  const canWrite = can('accounts', 'write');

  const [data, setData] = useState<{ stats?: Stats; accounts?: DashAccount[] } | null>(null);
  const [error, setError] = useState('');

  const [usageWin, setUsageWin] = useState<WindowKey>('7');
  const [usage, setUsage] = useState<{ total: number; usage: UsageDay[] } | null>(null);

  const [growthWin, setGrowthWin] = useState<WindowKey>('30');
  const [growth, setGrowth] = useState<{ data: GrowthDay[]; totalRegistrations: number; totalSites: number; totalCredits: number } | null>(null);

  const [activeWin, setActiveWin] = useState('1');
  const [active, setActive] = useState<{ days: number; count: number; accounts: ActiveAccount[] } | null>(null);

  useEffect(() => {
    backendApi.get<any>('/license/admin/dashboard').then(setData)
      .catch((err: ApiError) => setError(err.isForbidden ? 'You do not have access to the overview.' : err.message));
  }, []);

  useEffect(() => {
    setUsage(null);
    backendApi.get<any>(`/license/admin/usage-all?days=${daysParam(usageWin)}`).then(setUsage).catch(() => setUsage({ total: 0, usage: [] }));
  }, [usageWin]);

  useEffect(() => {
    setGrowth(null);
    backendApi.get<any>(`/license/admin/growth?days=${daysParam(growthWin)}`).then(setGrowth)
      .catch(() => setGrowth({ data: [], totalRegistrations: 0, totalSites: 0, totalCredits: 0 }));
  }, [growthWin]);

  useEffect(() => {
    setActive(null);
    backendApi.get<any>(`/license/admin/active-accounts?days=${activeWin}`).then(setActive)
      .catch(() => setActive({ days: Number(activeWin), count: 0, accounts: [] }));
  }, [activeWin]);

  if (error) return <div className="alert alert-error">{error}</div>;
  if (!data) return <p className="muted">Loading overview…</p>;

  const s = data.stats ?? {};
  const accounts = Array.isArray(data.accounts) ? data.accounts : [];
  const customers = s.customerAccounts ?? s.totalAccounts ?? accounts.length;
  const totalAcc = s.totalAccounts ?? accounts.length;
  const staff = s.accountsByType?.staff ?? 0;
  // Legacy "Credits Used" is customer-only (excludes staff), NOT the raw total.
  const creditsUsed = Number(s.customerCreditsUsed ?? s.totalCreditsUsed ?? 0);
  const creditsAvail = Number(s.totalCreditsBalance ?? 0);
  const sites = Number(s.totalSites ?? 0);

  return (
    <>
      <header className="page-head"><div><h1>Admin Dashboard</h1>
        <p className="muted">Manage licenses, accounts, and credits.</p></div></header>

      <div className="stat-row">
        <StatCard icon="users" tint="purple" value={customers} label="Customers"
          sub={staff ? `${staff} staff excluded · ${totalAcc} total` : `${totalAcc} total`} />
        <StatCard icon="clock" tint="green" value={creditsUsed.toLocaleString()} label="Credits Used"
          sub={`${creditsAvail.toLocaleString()} available`} />
        <StatCard icon="globe" tint="amber" value={sites} label="Active Sites" />
      </div>

      <div className="card">
        <div className="chart-head">
          <h2 className="card-title">Credits Usage</h2>
          <div className="chart-head-right">
            <span className="chart-total">{(usage?.total ?? 0).toLocaleString()} credits total</span>
            <WindowToggle value={usageWin} onChange={setUsageWin} />
          </div>
        </div>
        {!usage ? <p className="muted">Loading…</p> : <UsageChart usage={usage.usage} />}
      </div>

      <div className="card">
        <div className="chart-head">
          <h2 className="card-title">Growth (Cumulative)</h2>
          <WindowToggle value={growthWin} onChange={setGrowthWin} />
        </div>
        {!growth ? <p className="muted">Loading…</p> : <GrowthChart data={growth.data} />}
      </div>

      <div className="spark-row">
        <SparkCard title="User Registrations" data={growth?.data ?? []} field="registrations"
          color="#3B82F6" total={growth?.totalRegistrations ?? 0} />
        <SparkCard title="Site Activations" data={growth?.data ?? []} field="sites"
          color="#86DA9A" total={growth?.totalSites ?? 0} />
        <SparkCard title="Credits Usage" data={growth?.data ?? []} field="credits"
          color="#A78BFA" total={growth?.totalCredits ?? 0} />
      </div>

      <div className="card">
        <div className="card-head">
          <h2 className="card-title">Recent Accounts</h2>
          {canWrite && <Link className="btn btn-sm" to="/accounts">+ Add Account</Link>}
        </div>
        {accounts.length === 0 ? <p className="muted">No accounts yet.</p> : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Email</th><th>Plan</th><th>Credits</th><th>Sites</th><th>Status</th>
                {canWrite && <th></th>}</tr></thead>
              <tbody>
                {accounts.slice(0, 10).map((a) => {
                  const used = Number(a.creditsUsed ?? 0);
                  const left = Number(a.creditsBalance ?? 0);
                  return (
                    <tr key={a.id}>
                      <td>{a.email}
                        {(a.name || a.company) && <span className="muted small block">{[a.name, a.company].filter(Boolean).join(' · ')}</span>}</td>
                      <td><span className="badge badge-purple">{a.planName || a.plan || 'free'}</span></td>
                      <td className="mono">{used.toLocaleString()} used · {left.toLocaleString()} left</td>
                      <td className="mono">{Number(a.sitesCount ?? 0)}{a.maxSites ? ` / ${a.maxSites}` : ''}</td>
                      <td><span className={'badge ' + (a.status === 'active' ? 'badge-green' : 'badge-gray')}>{a.status}</span></td>
                      {canWrite && <td className="right"><Link className="btn btn-sm" to="/accounts">Edit</Link></td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-head">
          <div>
            <h2 className="card-title">Active Users</h2>
            <p className="muted small">Accounts that used credits in the window{active ? ` · ${active.count} active` : ''}</p>
          </div>
          <ActiveWindowToggle value={activeWin} onChange={setActiveWin} />
        </div>
        {!active ? <p className="muted">Loading…</p> : active.accounts.length === 0 ? <p className="muted">No active users in this window.</p> : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Email</th><th>Plan</th><th>Credits used</th><th>Events</th><th>Last active</th></tr></thead>
              <tbody>
                {active.accounts.map((a) => (
                  <tr key={a.id}>
                    <td>{a.email}
                      {a.name && <span className="muted small block">{a.name}</span>}</td>
                    <td><span className="badge badge-purple">{a.plan_id || 'free'}</span>
                      {a.account_type && a.account_type !== 'customer' && <span className="muted small block">{a.account_type}</span>}</td>
                    <td className="mono">{Number(a.credits_used).toLocaleString()}</td>
                    <td className="mono">{Number(a.events).toLocaleString()}</td>
                    <td className="muted">{fmtDateTime(a.last_active)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
